import { Base } from "./base";
import { ISurveyValidatorOwner, ISurvey } from "./base-interfaces";
import { SurveyError } from "./survey-error";
import { CustomError, RequreNumericError } from "./error";
import { LocalizableString } from "./localizablestring";
import { Serializer } from "./jsonobject";
import { property } from "./decorators";
import { ConditionRunner } from "./conditions/conditionRunner";
import { HashTable, Helpers } from "./helpers";
import { IValueGetterContext } from "./conditions/conditionProcessValue";

export class AsyncElementsRunner {
  private asyncElements: HashTable<boolean> = {};
  private isRunningValue: boolean;
  constructor (private onCompleted: () => void) {
    this.isRunningValue = true;
  }
  public addElement(id: string) {
    this.asyncElements[id] = true;
  }
  public removeElement(id: string) {
    delete this.asyncElements[id];
    this.tryComplete();
  }
  public finish(): void {
    this.isRunningValue = false;
    this.tryComplete();
  }
  public get isRunning(): boolean {
    return this.isRunningValue || Object.keys(this.asyncElements).length > 0;
  }
  protected doCompleted() {
    if (this.onCompleted) {
      this.onCompleted();
    }
  }
  private tryComplete(): void {
    if (!this.isRunning) {
      this.doCompleted();
    }
  }
}
export class ValidatorResult {
  constructor(public value: any, public error: SurveyError = null) {}
}
/**
 * A base class for all classes that implement validators.
 *
 * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
 */
export class SurveyValidator extends Base {
  public owner: ISurveyValidatorOwner;
  public get errorOwner(): ISurveyValidatorOwner { return this.owner; }
  public set errorOwner(val: ISurveyValidatorOwner) { this.owner = val; }
  public get id(): string { return "svd" + this.uniqueId; }
  public get isValidator(): boolean { return true; }
  public getSurvey(live: boolean = false): ISurvey {
    return !!this.owner && !!(<any>this.owner)["getSurvey"]
      ? (<any>this.owner).getSurvey()
      : null;
  }
  public getOwner() {
    return this.owner;
  }
  /**
   * Specifies the type of notification shown to users.
   *
   * Possible values:
   *
   * - `"error"`
   * - `"warning"`
   * - `"info"`
   *
   * Errors block survey progress until resolved. Warnings indicate potential issues but don't block respondents from continuing the survey. Informational notes provide guidance without restrictions.
   *
   * > If multiple notification types are eligible to be displayed for a question, only the strongest type is shown. Warnings appear only after all errors are resolved, and notes appear only when there are no errors or warnings.
   */
  @property() notificationType: string;
  /**
   * An error message to display when a value fails validation.
   */
  public get text(): string {
    return this.getLocStringText(this.locText);
  }
  public set text(value: string) {
    this.setLocStringText(this.locText, value);
  }
  get locText(): LocalizableString {
    return this.getOrCreateLocStr("text", true);
  }
  protected getErrorText(name: string): string {
    if (this.text) return this.text;
    return this.getDefaultErrorText(name);
  }
  protected getDefaultErrorText(name: string): string {
    return "";
  }
  public validateOnCallback(value: any, callback: (res: ValidatorResult) => void, name?: string, properties?: any): ValidatorResult {
    const res = this.validate(value, name, properties);
    if (callback) callback(res);
    return res;
  }
  public validate(value: any, name?: string, properties?: any): ValidatorResult {
    return null;
  }
  getLocale(): string {
    return !!this.owner ? this.owner.getLocale() : "";
  }
  getMarkdownHtml(text: string, name: string, item?: any): string {
    return !!this.owner
      ? this.owner.getMarkdownHtml(text, name, item)
      : undefined;
  }
  getRenderer(name: string): string {
    return !!this.owner ? this.owner.getRenderer(name) : null;
  }
  getRendererContext(locStr: LocalizableString): any {
    return !!this.owner ? this.owner.getRendererContext(locStr) : locStr;
  }
  getProcessedText(text: string): string {
    return !!this.owner ? this.owner.getProcessedText(text) : text;
  }
  protected createCustomError(name: string): SurveyError {
    const err = new CustomError(this.getErrorText(name), this.owner);
    err.onUpdateErrorTextCallback = (err => err.text = this.getErrorText(name));
    return err;
  }
  public toString(): string {
    var res = this.getType().replace("validator", "");
    if (!!this.text) {
      res += ", " + this.text;
    }
    return res;
  }
}
export interface IValidatorOwner {
  getValidators(): Array<SurveyValidator>;
  validatedValue: any;
  getValidatorTitle(): string;
  getDataFilteredProperties(): any;
}
export class ValidatorRunner {
  public onAsyncCompleted: (errors: Array<SurveyError>) => void;

  public run(owner: IValidatorOwner): Array<SurveyError> {
    const validators = owner.getValidators();
    const errors = new Array<SurveyError>();
    const asyncRunner = new AsyncElementsRunner(() => {
      if (this.onAsyncCompleted) {
        this.onAsyncCompleted(errors);
      }
    });
    if (validators.length > 0) {
      const properties = owner.getDataFilteredProperties();
      const value = owner.validatedValue;
      const title = owner.getValidatorTitle();
      validators.forEach(validator => {
        asyncRunner.addElement(validator.id);
        validator.validateOnCallback(value, (valRes: ValidatorResult): void => {
          if (!!valRes && !!valRes.error) {
            valRes.error.notificationType = validator.notificationType;
            errors.push(valRes.error);
          }
          asyncRunner.removeElement(validator.id);
        }, title, properties);
      });
    }
    const res = [].concat(...errors);
    errors.length = 0;
    asyncRunner.finish();
    return res;
  }
}
/**
 * A class that implements a validator for numeric values.
 *
 * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
 */
export class NumericValidator extends SurveyValidator {
  constructor(minValue: number | null = null, maxValue: number | null = null) {
    super();
    this.minValue = minValue;
    this.maxValue = maxValue;
  }
  public getType(): string {
    return "numericvalidator";
  }
  public validate(value: any, name?: string, properties?: any): ValidatorResult {
    if (this.isValueEmpty(value)) return null;
    if (!Helpers.isNumber(value)) {
      return new ValidatorResult(
        null,
        new RequreNumericError(this.text, this.owner)
      );
    }
    const result = new ValidatorResult(Helpers.getNumber(value));
    if (this.minValue !== null && this.minValue > result.value) {
      result.error = this.createCustomError(name);
      return result;
    }
    if (this.maxValue !== null && this.maxValue < result.value) {
      result.error = this.createCustomError(name);
      return result;
    }
    return typeof value === "number" ? null : result;
  }
  protected getDefaultErrorText(name: string) {
    var vName = name ? name : this.getLocalizationString("value");
    if (this.minValue !== null && this.maxValue !== null) {
      return this.getLocalizationFormatString("numericMinMax",
        vName, this.minValue, this.maxValue);
    } else {
      if (this.minValue !== null) {
        return this.getLocalizationFormatString("numericMin", vName, this.minValue);
      }
      return this.getLocalizationFormatString("numericMax", vName, this.maxValue);
    }
  }
  /**
   * A minimum allowed numeric value.
   *
   * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
   */
  @property() minValue: number | null;
  /**
   * A maximum allowed numeric value.
   *
   * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
   */
  @property() maxValue: number | null;
}
/**
 * A class that implements a validator for text values.
 *
 * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
 */
export class TextValidator extends SurveyValidator {
  constructor(
  ) {
    super();
  }
  public getType(): string {
    return "textvalidator";
  }
  public validate(value: any, name?: string, properties?: any): ValidatorResult {
    if (this.isValueEmpty(value)) return null;
    if (!this.allowDigits) {
      var reg = /\d+$/;
      if (reg.test(value)) {
        return new ValidatorResult(null, this.createCustomError("textNoDigitsAllow"));
      }
    }
    if (this.minLength > 0 && value.length < this.minLength) {
      return new ValidatorResult(null, this.createCustomError(name));
    }
    if (this.maxLength > 0 && value.length > this.maxLength) {
      return new ValidatorResult(null, this.createCustomError(name));
    }
    return null;
  }
  protected getDefaultErrorText(name: string): string {
    if (name === "textNoDigitsAllow") return this.getLocalizationString(name);
    if (this.minLength > 0 && this.maxLength > 0)
      return this.getLocalizationFormatString("textMinMaxLength", this.minLength, this.maxLength);
    if (this.minLength > 0)
      return this.getLocalizationFormatString("textMinLength", this.minLength);
    return this.getLocalizationFormatString("textMaxLength", this.maxLength);
  }
  /**
   * The minimum length of a text value measured in characters.
   *
   * Default value: 0
   *
   * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
   */
  @property() minLength: number;
  /**
   * The maximum length of a text value measured in characters.
   *
   * Default value: 0 (unlimited)
   *
   * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
   */
  @property() maxLength: number;
  /**
   * Specifies whether a text value can include numerical digits.
   *
   * Default value: `true`
   */
  @property() allowDigits: boolean;
}

/**
 * A class that implements answer count validation in the question types that can have multiple values (for instance, [Checkboxes](https://surveyjs.io/form-library/documentation/api-reference/checkbox-question-model)).
 *
 * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
 */
export class AnswerCountValidator extends SurveyValidator {
  constructor(minCount: number = null, maxCount: number = null) {
    super();
    this.minCount = minCount;
    this.maxCount = maxCount;
  }
  public getType(): string {
    return "answercountvalidator";
  }
  public validate(value: any, name?: string, properties?: any): ValidatorResult {
    if (value == null || value.constructor != Array) return null;
    var count = value.length;
    if (count == 0) return null;
    if (this.minCount && count < this.minCount) {
      return new ValidatorResult(
        null,
        this.createCustomError(
          this.getLocalizationFormatString("minSelectError", this.minCount)));
    }
    if (this.maxCount && count > this.maxCount) {
      return new ValidatorResult(
        null,
        this.createCustomError(
          this.getLocalizationFormatString("maxSelectError", this.maxCount)
        )
      );
    }
    return null;
  }
  protected getDefaultErrorText(name: string) {
    return name;
  }
  /**
   * A minimum number of selected answers.
   *
   * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
   */
  @property() minCount: number;
  /**
   * A maximum number of selected answers.
   *
   * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
   */
  @property() maxCount: number;
}
/**
 * A class that implements validation using regular expressions.
 *
 * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
 */
export class RegexValidator extends SurveyValidator {
  constructor(regex: string = null) {
    super();
    this.regex = regex;
  }
  public getType(): string {
    return "regexvalidator";
  }
  public validate(value: any, name?: string, properties?: any): ValidatorResult {
    if (!this.regex || this.isValueEmpty(value)) return null;
    const re = this.createRegExp();
    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const res = this.hasError(re, value[i], name);
        if (res) return res;
      }
    }
    return this.hasError(re, value, name);
  }
  private hasError(re: RegExp, value: any, name: string): ValidatorResult {
    if (re.test(value)) return null;
    return new ValidatorResult(value, this.createCustomError(name));
  }
  /**
   * A regular expression used to validate values.
   *
   * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
   */
  @property() regex: string;
  /**
   * Specifies whether uppercase and lowercase letters must be treated as distinct or equivalent when validating values.
   *
   * Default value: `false` (uppercase and lowercase letters are treated as distinct)
   */
  @property() caseInsensitive: boolean;
  public get insensitive(): boolean { return this.caseInsensitive; }
  public set insensitive(val: boolean) {
    this.caseInsensitive = val;
  }
  private createRegExp(): RegExp {
    const flags = this.caseInsensitive ? "i" : "";
    return (this.owner ? this.owner.createRegexValidator(this, this.regex, flags) : null) || new RegExp(this.regex, flags);
  }
}
/**
 * A class that implements a validator for e-mail addresses.
 *
 * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
 */
export class EmailValidator extends SurveyValidator {
  private re = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()=[\]\.,;:\s@\"]+\.)+[^<>()=[\]\.,;:\s@\"]{2,})$/i;
  constructor() {
    super();
  }
  public getType(): string {
    return "emailvalidator";
  }
  public validate(value: any, name?: string, properties?: any): ValidatorResult {
    if (!value) return null;
    if (this.re.test(value)) return null;
    return new ValidatorResult(value, this.createCustomError(name));
  }
  protected getDefaultErrorText(name: string): string {
    return this.getLocalizationString("invalidEmail");
  }
}

/**
 * A class that implements validation using [expressions](https://surveyjs.io/form-library/documentation/design-survey/conditional-logic#expressions).
 *
 * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
 */
export class ExpressionValidator extends SurveyValidator {
  private conditionRunner: ConditionRunner = null;
  constructor(expression: string = null) {
    super();
    this.expression = expression;
  }
  public getType(): string {
    return "expressionvalidator";
  }
  protected onPropertyValueChanged(name: string, oldValue: any, newValue: any): void {
    super.onPropertyValueChanged(name, oldValue, newValue);
    if (name === "expression") {
      const owner = <any>this.owner;
      if (!!owner && !!owner.resetValidationDependencies) {
        owner.resetValidationDependencies();
      }
    }
  }
  public validateOnCallback(value: any, callback: (res: ValidatorResult) => void, name?: string, properties?: any): ValidatorResult {
    if (!!this.conditionRunner) {
      this.conditionRunner.onRunComplete = null;
    }
    let errorResult: ValidatorResult = null;
    const doCallBack = (res: boolean) => {
      errorResult = this.generateError(res, value, name);
      !!callback && callback(errorResult);
    };
    if (!this.ensureConditionRunner()) {
      doCallBack(true);
      return null;
    }
    this.conditionRunner.onRunComplete = (res) => {
      doCallBack(res);
    };
    if (!this.conditionRunner.canRun()) {
      doCallBack(res);
      return errorResult;
    }
    var res = this.conditionRunner.runContext(this.getValueGetterContext(), this.getPropertiesCopy(properties, "expression"));
    return errorResult || this.generateError(res, value, name);
  }
  protected generateError(res: boolean, value: any, name: string): ValidatorResult {
    if (!res) {
      return new ValidatorResult(value, this.createCustomError(name));
    }
    return null;
  }
  protected getDefaultErrorText(name: string): string {
    return this.getLocalizationFormatString("invalidExpression", this.expression);
  }
  private ensureConditionRunner(): boolean {
    const expression = this.getExpressionFromSurvey("expression");
    if (!expression) return false;
    this.conditionRunner = new ConditionRunner(expression);
    return true;
  }
  /**
   * A Boolean [expression](https://surveyjs.io/form-library/documentation/design-survey/conditional-logic#expressions). If it evaluates to `false`, validation fails.
   *
   * [View Demo](https://surveyjs.io/form-library/examples/javascript-form-validation/ (linkStyle))
   */
  @property() expression: string;
  public getValueGetterContext(): IValueGetterContext {
    const owner = <any>this.owner;
    if (!!owner && !!owner.getValueGetterContext) return owner.getValueGetterContext();
    return super.getValueGetterContext();
  }
}

Serializer.addClass("surveyvalidator", [
  { name: "text", serializationProperty: "locText", visibleIndex: 99 },
  { name: "notificationType", choices: ["error", "warning", "info"], default: "error", visibleIndex: 100 }
]);
Serializer.addClass(
  "numericvalidator",
  ["minValue:number", "maxValue:number"],
  function() {
    return new NumericValidator();
  },
  "surveyvalidator"
);
Serializer.addClass(
  "textvalidator",
  [{ name: "minLength:number", default: 0 },
    { name: "maxLength:number", default: 0 },
    { name: "allowDigits:boolean", default: true }],
  function() {
    return new TextValidator();
  },
  "surveyvalidator"
);
Serializer.addClass(
  "answercountvalidator",
  ["minCount:number", "maxCount:number"],
  function() {
    return new AnswerCountValidator();
  },
  "surveyvalidator"
);
Serializer.addClass(
  "regexvalidator",
  ["regex", { name: "caseInsensitive:boolean", alternativeName: "insensitive" }],
  function() {
    return new RegexValidator();
  },
  "surveyvalidator"
);
Serializer.addClass(
  "emailvalidator",
  [],
  function() {
    return new EmailValidator();
  },
  "surveyvalidator"
);

Serializer.addClass(
  "expressionvalidator",
  ["expression:condition"],
  function() {
    return new ExpressionValidator();
  },
  "surveyvalidator"
);
